import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Boxes, Check, ClipboardCheck, FileText, Percent, Store, Truck, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { fmtGNF } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: FileText, title: "1. Déposez votre dossier", text: "Nom de boutique, contact, type (particulier ou société), NIF ou registre de commerce et photos." },
  { icon: ClipboardCheck, title: "2. Validation par EMPIRE-SHOP", text: "Nous étudions votre dossier. Vous recevez une notification dès la décision." },
  { icon: Store, title: "3. Publiez vos articles", text: "Vous gérez vos produits, prix de détail et prix de gros. Chaque article est contrôlé avant publication." },
  { icon: Wallet, title: "4. Vendez et encaissez", text: "Suivi des ventes en temps réel, commission transparente, versement Orange Money ou MTN." },
];

const BENEFITS = [
  { icon: Boxes, title: "Entrepôt de 300 m² en Guinée", text: "Stockage, préparation et étiquetage de vos colis depuis notre entrepôt." },
  { icon: Truck, title: "Livraison déjà organisée", text: "Nos livreurs couvrent la zone de l'entrepôt et les environs, paiement à la livraison inclus." },
  { icon: Percent, title: "Commission claire", text: "Un taux unique par catégorie, visible dans votre espace vendeur sur chaque vente." },
  { icon: BadgeCheck, title: "Contrôle qualité", text: "Validation des produits et accompagnement pour des fiches qui vendent." },
];

const FAQ = [
  ["Qui peut vendre sur EMPIRE-SHOP ?", "Tout commerçant, grossiste, artisan ou société en Guinée, après validation de son dossier par l'administrateur."],
  ["Combien ça coûte ?", "L'inscription et le forfait Gratuit ne coûtent rien : nous prélevons seulement une commission sur les ventes. Les forfaits Pro et Premium, payants au mois, publient vos articles immédiatement."],
  ["Comment suis-je payé ?", "Vos ventes livrées alimentent votre solde. Les versements sont effectués par Orange Money, MTN Mobile Money ou en espèces à l'entrepôt."],
  ["Qui gère la livraison ?", "EMPIRE-SHOP s'occupe de la préparation et de la livraison au client, avec suivi du statut de chaque commande."],
  ["Puis-je vendre en gros ?", "Oui. Chaque produit peut avoir un prix de gros et une quantité minimum, affichés automatiquement au client."],
];

const PLAN_PERKS = {
  gratuit: [
    "Boutique vendeur et fiches produits illimitées",
    "Chaque article validé par EMPIRE-SHOP avant publication",
    "Suivi des ventes, commissions et solde",
  ],
  pro: [
    "Publication immédiate de vos articles, sans attente",
    "Mise en avant possible dans « Les articles du moment »",
    "Suivi des ventes, commissions et solde",
    "Stockage et préparation à l'entrepôt EMPIRE-SHOP",
  ],
  premium: [
    "Publication immédiate de vos articles, sans attente",
    "Priorité d'affichage et mises en avant régulières",
    "Accompagnement dédié sur vos fiches produits",
    "Priorité sur la préparation et les tournées de livraison",
  ],
};

export default function Professionals() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/vendor/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  const ctaTo = !user ? "/inscription" : user.role === "vendor" ? "/vendeur" : "/devenir-vendeur";
  const ctaLabel = !user
    ? "Créer un compte et postuler"
    : user.role === "vendor"
    ? "Accéder à mon espace vendeur"
    : "Déposer ma demande";

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="professionals-page">
      <section className="ds-card overflow-hidden">
        <div className="bg-brand px-5 py-8 text-white sm:px-8 sm:py-10">
          <p className="ds-label text-emerald-100">Espace professionnel</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
            Vendez vos produits sur <span className="text-gold">EMPIRE-SHOP</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-emerald-50 md:text-base">
            Rejoignez la marketplace guinéenne du détail et du gros. Vous apportez les produits,
            nous apportons les clients, l'entrepôt et la livraison — avec validation de chaque dossier par l'administrateur.
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Link to={ctaTo} className="ds-btn-gold" data-testid="pro-cta-primary">
              <Store className="h-4 w-4" /> {ctaLabel}
            </Link>
            <Link to="/catalogue" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/40 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10" data-testid="pro-cta-catalog">
              Voir le catalogue
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">Pourquoi vendre avec nous</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {BENEFITS.map((b) => (
            <div key={b.title} className="ds-card flex gap-3 p-4" data-testid={`pro-benefit-${b.title}`}>
              <b.icon className="mt-0.5 h-6 w-6 shrink-0 text-brand" />
              <div>
                <p className="text-sm font-semibold text-slate-900">{b.title}</p>
                <p className="mt-1 text-sm text-slate-600">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">Comment ça marche</h2>
        <ol className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s) => (
            <li key={s.title} className="ds-card p-4" data-testid={`pro-step-${s.title}`}>
              <s.icon className="h-6 w-6 text-gold" />
              <p className="mt-2 text-sm font-bold text-slate-900">{s.title}</p>
              <p className="mt-1 text-sm text-slate-600">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {plans.length > 0 && (
        <section className="mt-8" data-testid="pro-plans-section">
          <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">Nos forfaits vendeurs</h2>
          <p className="mt-1 text-sm text-slate-600">
            L'inscription est gratuite. Les forfaits Pro et Premium publient vos articles immédiatement,
            sans attendre la validation de EMPIRE-SHOP.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`ds-card flex flex-col p-4 ${p.id === "pro" ? "border-2 border-gold" : ""}`}
                data-testid={`pro-plan-${p.id}`}
              >
                {p.id === "pro" && (
                  <span className="ds-chip w-fit border-gold bg-gold-light text-gold-dark">Le plus choisi</span>
                )}
                <p className="mt-1 font-display text-lg font-bold text-slate-900">{p.label}</p>
                <p className="font-num mt-1 text-2xl font-extrabold text-brand">
                  {p.monthly_fee === 0 ? "0 GNF" : fmtGNF(p.monthly_fee)}
                  <span className="text-xs font-medium text-slate-500">{p.monthly_fee === 0 ? " / toujours" : " / mois"}</span>
                </p>
                <p className="mt-1 text-xs font-semibold text-slate-600">
                  {p.auto_publish ? "Publication immédiate" : "Validation EMPIRE-SHOP avant publication"}
                </p>
                <ul className="mt-3 flex-1 space-y-1.5">
                  {(PLAN_PERKS[p.id] || []).map((perk) => (
                    <li key={perk} className="flex gap-2 text-xs text-slate-600">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /> {perk}
                    </li>
                  ))}
                </ul>
                <Link to={ctaTo} className={`mt-4 w-full ${p.id === "pro" ? "ds-btn-gold" : "ds-btn-ghost"}`} data-testid={`pro-plan-cta-${p.id}`}>
                  {p.monthly_fee === 0 ? "Commencer gratuitement" : "Choisir ce forfait"}
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500" data-testid="pro-plans-note">
            Une commission sur chaque vente s'ajoute au forfait. Le changement de forfait se fait avec
            l'administrateur EMPIRE-SHOP, qui l'active sur votre compte vendeur.
          </p>
        </section>
      )}

      <section className="mt-8">
        <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">Questions fréquentes</h2>
        <div className="mt-3 space-y-2">
          {FAQ.map(([q, a]) => (
            <details key={q} className="ds-card p-4" data-testid={`pro-faq-${q}`}>
              <summary className="cursor-pointer text-sm font-semibold text-slate-900">{q}</summary>
              <p className="mt-2 text-sm text-slate-600">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="ds-card mt-8 flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-display text-lg font-bold text-slate-900">Prêt à vendre en Guinée ?</p>
          <p className="mt-1 text-sm text-slate-600">Dossier étudié sous 48 h ouvrées par l'administrateur EMPIRE-SHOP.</p>
        </div>
        <Link to={ctaTo} className="ds-btn-primary" data-testid="pro-cta-bottom">
          <Store className="h-4 w-4" /> {ctaLabel}
        </Link>
      </section>
    </div>
  );
}
